import React, { useState } from 'react'
import { Button } from './ui/button'
import { Download, FileText, ChevronDown, FileType, FileIcon, Globe, FileJson } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './ui/dropdown-menu'

interface ExportButtonProps {
  proposal: string
  projectId: string
  onExport?: (format: string) => void
}

export function ExportButton({ proposal, projectId, onExport }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false)

  const promptForFilename = (format: string, watermark?: 'DRAFT' | 'FINAL'): string | null => {
    const timestamp = new Date().toISOString().split('T')[0]
    const watermarkSuffix = watermark ? `_${watermark.toLowerCase()}` : ''
    const defaultName = `proposal_${projectId}${watermarkSuffix}_${timestamp}`
    
    const customName = window.prompt(
      `Enter filename (without extension):`,
      defaultName
    )
    
    if (customName === null) {
      // User cancelled
      return null
    }
    
    // Clean the filename (remove invalid characters and extension if provided)
    const cleaned = customName
      .replace(/\.[^/.]+$/, '') // Remove extension if user added it
      .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
      .trim()
    
    return cleaned || defaultName // Use default if empty after cleaning
  }

  const handleExport = async (format: 'markdown' | 'pdf' | 'docx' | 'html' | 'json', watermark?: 'DRAFT' | 'FINAL') => {
    // Prompt for custom filename
    const filename = promptForFilename(format, watermark)
    if (filename === null) {
      return // User cancelled
    }

    setIsExporting(true)
    try {
      if (format === 'markdown') {
        // Client-side Markdown export
        const blob = new Blob([proposal], { type: 'text/markdown' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${filename}.md`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } else if (format === 'pdf' || format === 'docx' || format === 'html' || format === 'json') {
        // Server-side PDF/DOCX/HTML/JSON export
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'
        const token = localStorage.getItem('access_token')
        
        // Add watermark parameter if specified (only for PDF and DOCX)
        const watermarkParam = watermark && (format === 'pdf' || format === 'docx') 
          ? `?watermark=${watermark}` 
          : ''
        
        const response = await fetch(`${apiUrl}/api/project/${projectId}/export/${format}${watermarkParam}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        
        if (!response.ok) {
          throw new Error(`${format.toUpperCase()} export failed: ${response.statusText}`)
        }
        
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${filename}.${format}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }

      if (onExport) {
        onExport(format)
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={isExporting}>
          <Download className="w-4 h-4 mr-2" />
          Export
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExport('markdown')}>
          <FileText className="w-4 h-4 mr-2" />
          Markdown (.md)
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel>PDF Export</DropdownMenuLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <FileType className="w-4 h-4 mr-2" />
            PDF (.pdf)
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleExport('pdf')}>
              Draft
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('pdf', 'FINAL')}>
              Final
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Word Export</DropdownMenuLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <FileIcon className="w-4 h-4 mr-2" />
            Word (.docx)
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleExport('docx')}>
              Draft
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('docx', 'FINAL')}>
              Final
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleExport('html')}>
          <Globe className="w-4 h-4 mr-2" />
          HTML (.html)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('json')}>
          <FileJson className="w-4 h-4 mr-2" />
          JSON (.json)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
